import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Mic, MicOff, X, Loader2, Volume2, VolumeX, Sunrise, Check, Sparkles, Car, Send,
} from "lucide-react";

const SR = typeof window !== "undefined" ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;

export default function CrashReadyVoice() {
  const { user } = useAuth();
  const roles = user?.roles || [user?.role];
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [listening, setListening] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [driving, setDriving] = useState(false);
  const [muted, setMuted] = useState(false);
  const [pending, setPending] = useState(false);
  const [text, setText] = useState("");
  const recRef = useRef(null);
  const scrollRef = useRef(null);
  const mutedRef = useRef(false);
  const listenStartRef = useRef(null);
  useEffect(() => { mutedRef.current = muted; if (muted) window.speechSynthesis?.cancel(); }, [muted]);

  const deviceId = useMemo(() => {
    let id = localStorage.getItem("cr_device_id");
    if (!id) { id = "dev_" + Math.random().toString(36).slice(2, 10); localStorage.setItem("cr_device_id", id); }
    return id;
  }, []);
  const platform = typeof navigator !== "undefined"
    ? (navigator.userAgentData?.platform || navigator.platform || "web") : "web";
  const APP_VERSION = "1.0";

  const isAttorney = roles?.includes("attorney") || roles?.includes("admin");

  const speak = useCallback((t) => {
    if (mutedRef.current || !t || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(t);
    u.rate = 1.05; u.pitch = 1;
    window.speechSynthesis.speak(u);
  }, []);

  const startSession = useCallback(async () => {
    try {
      const { data } = await api.post("/voice/session", { device: "web", resume_latest: true });
      setSessionId(data.session_id);
      setMessages(data.messages || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { if (open && !sessionId) startSession(); }, [open, sessionId, startSession]);
  useEffect(() => { scrollRef.current?.scrollTo({ top: 9e9, behavior: "smooth" }); }, [messages, thinking]);

  const send = useCallback(async (t, durationMs = null) => {
    const q = (t || "").trim();
    if (!q) return;
    setMessages((m) => [...m, { role: "attorney", content: q }]);
    setText(""); setThinking(true);
    try {
      const { data } = await api.post("/voice/command", {
        session_id: sessionId, text: q, device: "web", driving,
        device_id: deviceId, platform, app_version: APP_VERSION, duration_ms: durationMs,
      });
      if (!sessionId) setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.spoken_response }]);
      setPending(!!data.requires_confirmation);
      speak(data.spoken_response);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry, I couldn't process that." }]);
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setThinking(false); }
  }, [sessionId, driving, speak, deviceId, platform]);

  const confirm = async () => {
    setThinking(true); setPending(false);
    try {
      const { data } = await api.post("/voice/confirm", { session_id: sessionId, device_id: deviceId, platform, app_version: APP_VERSION });
      setMessages((m) => [...m, { role: "assistant", content: data.spoken_response }]);
      speak(data.spoken_response);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } finally { setThinking(false); }
  };

  const cancelPending = async () => {
    setPending(false); setThinking(true);
    try {
      const { data } = await api.post("/voice/cancel", { session_id: sessionId, device_id: deviceId, platform, app_version: APP_VERSION });
      setMessages((m) => [...m, { role: "assistant", content: data.spoken_response }]);
      speak(data.spoken_response);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } finally { setThinking(false); }
  };

  const briefing = async () => {
    setThinking(true);
    try {
      const { data } = await api.get("/voice/briefing");
      setMessages((m) => [...m, { role: "assistant", content: data.spoken_response }]);
      speak(data.spoken_response);
    } catch { /* ignore */ } finally { setThinking(false); }
  };

  const toggleMic = () => {
    if (!SR) { toast.error("Voice input isn't supported in this browser — type your command."); return; }
    if (listening) { recRef.current?.stop(); return; }
    const rec = new SR();
    rec.lang = "en-US"; rec.interimResults = false; rec.maxAlternatives = 1;
    rec.onresult = (e) => { const t = e.results[0][0].transcript; const ms = listenStartRef.current ? Date.now() - listenStartRef.current : null; send(t, ms); };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    recRef.current = rec; setListening(true);
    listenStartRef.current = Date.now();
    window.speechSynthesis?.cancel();
    rec.start();
  };

  if (!isAttorney) return null;

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="voice-fab"
        className="fixed bottom-6 right-6 z-40 h-14 w-14 rounded-full bg-cobalt text-white shadow-lg shadow-cobalt/30 flex items-center justify-center hover:bg-cobalt-hover active:scale-95 transition-all"
        aria-label="Crash Ready Voice">
        <Sparkles className="w-6 h-6" />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 24, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24, scale: 0.98 }}
            className="fixed bottom-6 right-6 z-50 w-[calc(100vw-2rem)] sm:w-96 h-[560px] max-h-[85vh] bg-navy text-slate-100 rounded-3xl border border-slate-800 shadow-2xl flex flex-col overflow-hidden" data-testid="voice-panel">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800 bg-gradient-to-r from-cobalt/20 to-transparent">
              <div>
                <p className="text-[11px] font-medium text-cyan-300 uppercase tracking-widest flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> Crash Ready Voice™</p>
                <p className="text-xs text-slate-400">Your AI legal assistant, on every device</p>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => setDriving((d) => !d)} title="Driving mode" data-testid="voice-driving"
                  className={`p-2 rounded-full ${driving ? "bg-amber-500/20 text-amber-300" : "text-slate-400 hover:bg-slate-800"}`}><Car className="w-4 h-4" /></button>
                <button onClick={() => setMuted((m) => !m)} title="Mute voice" data-testid="voice-mute"
                  className="p-2 rounded-full text-slate-400 hover:bg-slate-800">{muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}</button>
                <button onClick={() => { setOpen(false); window.speechSynthesis?.cancel(); }} className="p-2 rounded-full text-slate-400 hover:bg-slate-800"><X className="w-5 h-5" /></button>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="voice-transcript">
              {messages.length === 0 && (
                <div className="text-center text-slate-400 mt-8 px-4">
                  <div className="h-14 w-14 rounded-2xl bg-cobalt/15 mx-auto flex items-center justify-center mb-3"><Mic className="w-7 h-7 text-cyan-300" /></div>
                  <p className="text-sm">Tap the mic or type. Try:</p>
                  <div className="mt-3 space-y-1.5 text-xs">
                    {["What's waiting for me?", "How many demand-ready cases do I have?", "What should I work on next?"].map((s) => (
                      <button key={s} onClick={() => send(s)} className="block w-full px-3 py-1.5 rounded-full bg-slate-800/60 text-slate-300 hover:bg-slate-800">{s}</button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "attorney" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] px-3.5 py-2 rounded-2xl text-sm leading-relaxed ${m.role === "attorney" ? "bg-cobalt text-white rounded-br-sm" : "bg-slate-800 text-slate-100 rounded-bl-sm"}`}>{m.content}</div>
                </div>
              ))}
              {thinking && <div className="flex justify-start"><div className="bg-slate-800 rounded-2xl px-3 py-2"><Loader2 className="w-4 h-4 animate-spin text-cyan-300" /></div></div>}
              {pending && !thinking && (
                <div className="flex gap-2 justify-start" data-testid="voice-confirm-actions">
                  <button onClick={confirm} data-testid="voice-confirm" className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-emerald-500 text-slate-950 text-sm font-semibold"><Check className="w-4 h-4" /> Confirm</button>
                  <button onClick={cancelPending} data-testid="voice-cancel" className="px-4 py-2 rounded-full bg-slate-800 text-slate-300 text-sm">Cancel</button>
                </div>
              )}
            </div>

            <div className="p-3 border-t border-slate-800 space-y-2">
              <button onClick={briefing} data-testid="voice-briefing" className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-full bg-slate-800/70 text-cyan-300 text-xs font-medium hover:bg-slate-800"><Sunrise className="w-4 h-4" /> Daily Briefing</button>
              <div className="flex items-center gap-2">
                <button onClick={toggleMic} data-testid="voice-mic"
                  className={`h-11 w-11 rounded-full flex items-center justify-center shrink-0 transition-all ${listening ? "bg-red-500 animate-pulse" : "bg-cobalt hover:bg-cobalt-hover"}`}>
                  {listening ? <MicOff className="w-5 h-5 text-white" /> : <Mic className="w-5 h-5 text-white" />}
                </button>
                <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send(text)}
                  data-testid="voice-input" placeholder={listening ? "Listening…" : "Ask or command…"}
                  className="flex-1 bg-slate-800 border border-slate-700 rounded-full px-4 py-2.5 text-sm text-white outline-none focus:border-cobalt" />
                <button onClick={() => send(text)} disabled={!text.trim()} data-testid="voice-send" className="h-11 w-11 rounded-full bg-slate-800 text-cyan-300 flex items-center justify-center disabled:opacity-40"><Send className="w-4 h-4" /></button>
              </div>
              {driving && <p className="text-[11px] text-amber-300 text-center">Driving mode — concise replies, hands-free.</p>}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
